import { memo, useId, useState } from 'react';
import type { CustomerDTO } from '@six-pockets/shared';
import { PlusIcon, SearchIcon } from '@/components/icons';
import { Section, TextField } from '@/components/ui';
import { formatPhone } from '@/utils/format';
import { useCustomerSearch } from '../useCustomerSearch';
import { CustomerCard } from './CustomerCard';
import { NewCustomerForm } from './NewCustomerForm';
import styles from '@/styles/components/CustomerPicker/style.scss';

interface CustomerPickerProps {
  /** The heading while no customer is picked. */
  label?: string;
  /** The heading once one is. */
  selectedLabel?: string;
  value: CustomerDTO | null;
  onChange: (customer: CustomerDTO | null) => void;
}

/**
 * Search customers by name or mobile number, pick one, or add a new one.
 * Three states: selected card · search box with results · new-customer form.
 * Memoised: in the bill it re-renders only for its own value, not for typing in the totals or payment.
 */
export const CustomerPicker = memo(function CustomerPicker({ label = 'Select customer', selectedLabel = 'Billed customer', value, onChange }: CustomerPickerProps) {
  const inputId = useId();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const { results, loading, error, noMatches } = useCustomerSearch(value || adding ? '' : query);

  const pick = (c: CustomerDTO) => {
    onChange(c);
    setQuery('');
    setAdding(false);
  };

  const showDropdown = !value && !adding && query.trim().length > 0;

  return (
    <Section title={value ? selectedLabel : label} htmlFor={value || adding ? undefined : inputId}>
      {value ? (
        <CustomerCard customer={value} onChange={() => onChange(null)} />
      ) : adding ? (
        <NewCustomerForm initialQuery={query} onCreated={pick} onCancel={() => setAdding(false)} />
      ) : (
        <div className={styles.search}>
          <TextField
            id={inputId}
            type="search"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showDropdown}
            aria-controls={listId}
            autoComplete="off"
            placeholder="Search customer by name or mobile number"
            prefix={<SearchIcon size={18} />}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {showDropdown && (
            <div className={styles.dropdown}>
              <ul id={listId} role="listbox" aria-label="Matching customers" className={styles.results}>
                {results.map((c) => (
                  <li key={c.id} role="option" aria-selected={false}>
                    <button type="button" className={styles.result} onClick={() => pick(c)}>
                      <span className={styles.resultName}>{c.name}</span>
                      <span className={styles.resultPhone}>{formatPhone(c.phone)}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {loading && <div className={styles.status}>Searching…</div>}
              {error && <div className={styles.status}>{error}</div>}
              {noMatches && <div className={styles.status}>No customer found for “{query.trim()}”</div>}
              <button type="button" className={styles.add} onClick={() => setAdding(true)}>
                <PlusIcon size={16} />
                Add new customer
              </button>
            </div>
          )}
        </div>
      )}
    </Section>
  );
});
